import { useEffect } from 'react';
import { prefetchRouteOnIdle } from '@/components/shared/PrefetchLink';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ArticleCard } from '@/components/shared/ArticleCard';
import { transformImageUrl, buildSrcSet, fallbackToOrigin } from '@/lib/imageUrl';
import { EventCard } from '@/components/shared/EventCard';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { NewsletterSignup } from '@/components/shared/NewsletterSignup';
import { 
  useCachedArticles, 
  useCachedHeroArticle, 
  useCachedEvents 
} from '@/hooks/useCachedContent';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Clock, Car, Gauge, ArrowRight, Sparkles, Shield, Brain, BookOpen, Lock, FileSearch } from 'lucide-react';
import { format } from 'date-fns';
import { SEOHead } from '@/components/seo/SEOHead';
import { OrganizationSchema } from '@/components/seo/StructuredData';

export default function HomePage() {
  const { data: heroArticle, isLoading: heroLoading } = useCachedHeroArticle();
  const { data: latestNews, isLoading: newsLoading } = useCachedArticles({ 
    contentType: 'news', 
    limit: 6 
  });
  const { data: featuredArticles, isLoading: featuredLoading } = useCachedArticles({ 
    featured: true, 
    limit: 4 
  });
  const { data: upcomingEvents, isLoading: eventsLoading } = useCachedEvents({ 
    upcoming: true, 
    limit: 2 
  });

  // Preload hero image for LCP optimization
  useEffect(() => {
    const heroImageUrl = heroArticle?.hero_image_url || heroArticle?.thumbnail_url;
    if (heroImageUrl) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = heroImageUrl;
      link.fetchPriority = 'high';
      // Only add if not already present
      if (!document.querySelector(`link[href="${heroImageUrl}"]`)) {
        document.head.appendChild(link);
      }
    }
  }, [heroArticle]);

  // Warm popular next-route chunks while the browser is idle so navigation
  // from the homepage feels instant. Safe: only runs in requestIdleCallback.
  useEffect(() => {
    prefetchRouteOnIdle('/news');
    prefetchRouteOnIdle('/reviews/2026');
    prefetchRouteOnIdle('/events');
  }, []);


  return (
    <Layout>
      <SEOHead
        title="DealIQ — Know if your car deal is realistic before the dealership"
        description="DealIQ helps you research your finance, lease, or cash offer in minutes. No account, no dealer submission, no credit pull. Plus 2026 vehicle review summaries from across the auto industry."
        canonical="https://www.autonewsweekly.com/"
      />
      <OrganizationSchema />
      
      {/* Modern Split Hero - News + Deal IQ */}
      <section className="relative bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
            {/* Main Hero Article - Takes 3/5 on desktop */}
            <div className="lg:col-span-3">
              {heroLoading ? (
                <Skeleton className="h-[300px] md:h-[400px] lg:h-[500px] rounded-2xl" />
              ) : heroArticle ? (
                <Link 
                  to={`/news/${heroArticle.slug}`}
                  className="block relative h-[300px] md:h-[400px] lg:h-[500px] rounded-2xl overflow-hidden group"
                >
                  {heroArticle.hero_image_url || heroArticle.thumbnail_url ? (
                    <img
                      src={transformImageUrl(heroArticle.hero_image_url || heroArticle.thumbnail_url, { width: 1200 })}
                      srcSet={buildSrcSet(heroArticle.hero_image_url || heroArticle.thumbnail_url, [640, 1024, 1200, 1600])}
                      alt={heroArticle.title}
                      className="absolute inset-0 w-full h-full object-cover object-[center_70%] transition-transform duration-700 group-hover:scale-105"
                      width={1200}
                      height={675}
                      loading="eager"
                      {...({ fetchpriority: "high" } as Record<string, string>)}
                      decoding="async"
                      sizes="(max-width: 1024px) 100vw, 60vw"
                      onError={(e) => fallbackToOrigin(e, heroArticle.hero_image_url || heroArticle.thumbnail_url)}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary/80" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
                    <div className="flex items-center gap-2 mb-3">
                      {heroArticle.categories?.[0] && (
                        <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-semibold rounded-full">
                          {heroArticle.categories[0].name}
                        </span>
                      )}
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                        Featured
                      </span>
                    </div>
                    <h1 className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white max-w-2xl mb-3 leading-tight">
                      {heroArticle.title}
                    </h1>
                    {heroArticle.subtitle && (
                      <p className="text-sm md:text-base text-white/80 max-w-xl mb-4 line-clamp-2">
                        {heroArticle.subtitle}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs md:text-sm text-white/70">
                      {heroArticle.published_at && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" />
                          <time dateTime={heroArticle.published_at}>
                            {format(new Date(heroArticle.published_at), 'MMM d, yyyy')}
                          </time>
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        {heroArticle.read_time_minutes} min read
                      </span>
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="h-[300px] md:h-[400px] lg:h-[500px] bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center">
                  <div className="text-center text-primary-foreground p-6">
                    <h1 className="font-display text-3xl md:text-5xl font-bold mb-3">Auto News Weekly</h1>
                    <p className="text-lg text-primary-foreground/80">Your source for automotive industry news</p>
                  </div>
                </div>
              )}
            </div>

            {/* Deal IQ Promo Card - Takes 2/5 on desktop */}
            <div className="lg:col-span-2">
              <Card className="h-full bg-gradient-to-br from-primary via-primary to-primary/90 border-0 text-primary-foreground overflow-hidden relative">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />
                
                <CardContent className="p-6 md:p-8 h-full flex flex-col relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <span className="text-sm font-semibold text-white/90">Research Tool</span>
                    <span className="px-2 py-0.5 bg-amber-400/20 border border-amber-400/40 text-amber-200 text-[10px] font-bold uppercase tracking-wide rounded-full">
                      Patent Pending
                    </span>
                  </div>
                  
                  <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight">
                    Is your car deal realistic?
                  </h2>
                  <p className="text-base md:text-lg text-white/85 mb-6 leading-relaxed">
                    DealIQ helps you research your finance, lease, or cash offer in minutes — no account, no dealer submission, no credit pull.
                  </p>
                  
                  <div className="space-y-3 mb-6 flex-grow">
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-white/20 rounded-full">
                        <Gauge className="h-4 w-4" />
                      </div>
                      <span className="text-sm text-white/90">Instant deal realism score</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-white/20 rounded-full">
                        <Brain className="h-4 w-4" />
                      </div>
                      <span className="text-sm text-white/90">Talking points for the dealer</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-white/20 rounded-full">
                        <Lock className="h-4 w-4" />
                      </div>
                      <span className="text-sm text-white/90">No account · No credit pull</span>
                    </div>
                  </div>
                  
                  <Link to="/deals/builder/create?mode=new" className="block">
                    <Button 
                      size="lg" 
                      variant="secondary" 
                      className="w-full gap-2 font-semibold text-primary hover:text-primary"
                    >
                      Check My Deal
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 py-8 md:py-12">
        {/* Latest News Section - Enhanced Grid */}
        <section className="mb-12 md:mb-16" aria-labelledby="latest-news-heading">
          <SectionHeader title="Latest News & Insights" viewAllLink="/news" id="latest-news-heading" />
          {newsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-[280px] md:h-[350px] rounded-xl" />
              ))}
            </div>
          ) : latestNews && latestNews.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {latestNews.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No news articles yet.</p>
          )}
        </section>

        {/* How Deal IQ Works - Modern Steps */}
        <section className="mb-12 md:mb-16 relative overflow-hidden" aria-labelledby="how-it-works-heading">
          <div className="bg-gradient-to-r from-muted/80 via-muted to-muted/80 rounded-2xl md:rounded-3xl p-6 md:p-10 lg:p-14">
            <div className="text-center mb-8 md:mb-12">
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-xs font-semibold rounded-full mb-3">
                How It Works
              </span>
              <h2 id="how-it-works-heading" className="font-display text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
                A smarter way to research your car deal
              </h2>
              <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
                No account. No credit pull. No dealer submission. Just research.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-12 mb-8 md:mb-12">
              {[
                { 
                  step: 1, 
                  icon: Car, 
                  title: 'Enter your deal', 
                  desc: 'Add the vehicle, price, down payment, trade-in, payment target, and basic buyer context.' 
                },
                { 
                  step: 2, 
                  icon: Gauge, 
                  title: 'Get a DealIQ result', 
                  desc: 'See whether your deal looks Strong, Fair, Challenging, or Unlikely based on key approval and market factors.' 
                },
                { 
                  step: 3, 
                  icon: Shield, 
                  title: 'Negotiate with confidence', 
                  desc: "Use DealIQ's insights to understand what to adjust before you talk to the dealer." 
                },
              ].map((item) => (
                <div key={item.step} className="relative text-center">
                  {/* Step Number */}
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                    {item.step}
                  </div>
                  
                  <Card className="pt-8 h-full border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-5 md:p-6">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <item.icon className="h-7 w-7 md:h-8 md:w-8 text-primary" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <Link to="/deals/builder/create?mode=new">
                <Button size="lg" className="gap-2 px-8 font-semibold shadow-lg hover:shadow-xl transition-shadow">
                  Check My Deal
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* 2026 Vehicle Review Hub teaser */}
        <section className="mb-12 md:mb-16" aria-labelledby="review-hub-heading">
          <Card className="border-0 overflow-hidden bg-gradient-to-br from-secondary via-secondary to-muted">
            <CardContent className="p-6 md:p-10 lg:p-12">
              <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full mb-4">
                    <BookOpen className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold text-primary">New Car Review Hub</span>
                  </div>
                  <h2 id="review-hub-heading" className="font-display text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
                    One place to understand what the auto world is saying.
                  </h2>
                  <p className="text-muted-foreground text-base md:text-lg mb-6">
                    DealIQ brings together expert reviews, video opinions, vehicle scores, and shopper-focused
                    insights for 2026 vehicles — so you can research the car and the deal in one place.
                  </p>
                  <Link to="/reviews/2026">
                    <Button size="lg" variant="default" className="gap-2">
                      Explore New Car Reviews
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Brain, label: 'Reviewer consensus' },
                    { icon: Gauge, label: 'DealIQ vehicle scores' },
                    { icon: FileSearch, label: 'Pros & cons summaries' },
                    { icon: Car, label: 'Buyer fit recommendations' },
                  ].map((item) => (
                    <div key={item.label} className="bg-background/60 rounded-xl p-4 flex flex-col gap-2">
                      <item.icon className="h-5 w-5 text-primary" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Trust strip */}
        <section className="mb-12 md:mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { icon: Lock, title: 'No account required', desc: 'Use DealIQ without signing up.' },
              { icon: Shield, title: 'No credit pull', desc: 'We never run a credit check.' },
              { icon: Sparkles, title: 'No dealer submission', desc: 'Your research stays private.' },
              { icon: Gauge, title: 'No obligation', desc: "It's research, not a contract." },
            ].map((item) => (
              <Card key={item.title} className="border-0 shadow-sm">
                <CardContent className="p-4 md:p-5">
                  <item.icon className="h-5 w-5 text-primary mb-2" />
                  <p className="font-semibold text-sm md:text-base">{item.title}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-4 text-[11px] md:text-xs text-muted-foreground text-center max-w-3xl mx-auto leading-relaxed">
            DealIQ provides research and educational estimates only. DealIQ is not a lender, dealership, broker, or
            credit decisioning platform and does not guarantee financing, lease approval, or dealer acceptance.
          </p>
        </section>

        {/* Featured Articles */}
        <section className="mb-12 md:mb-16" aria-labelledby="featured-heading">
          <SectionHeader title="Featured Articles" viewAllLink="/featured" id="featured-heading" />
          {featuredLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-[250px] md:h-[300px] rounded-xl" />
              ))}
            </div>
          ) : featuredArticles && featuredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {featuredArticles.slice(0, 2).map((article) => (
                <ArticleCard key={article.id} article={article} variant="featured" />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No featured articles yet.</p>
          )}
        </section>

        {/* Newsletter - Modern Design */}
        <section className="mb-12 md:mb-16" aria-labelledby="newsletter-heading">
          <Card className="border-0 bg-gradient-to-br from-secondary via-secondary to-muted overflow-hidden">
            <CardContent className="p-6 md:p-10 lg:p-12">
              <div className="max-w-2xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 rounded-full mb-4">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-xs font-semibold text-primary">Weekly Insights</span>
                </div>
                <h2 id="newsletter-heading" className="font-display text-2xl md:text-3xl font-bold mb-3">
                  Stay Ahead of the Curve
                </h2>
                <p className="text-muted-foreground text-base md:text-lg mb-6">
                  Get exclusive automotive insights, deal tips, and industry news delivered directly to your inbox.
                </p>
                <NewsletterSignup showPreferences />
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Upcoming Events */}
        <section className="mb-10 md:mb-16" aria-labelledby="events-heading">
          <SectionHeader title="Upcoming Events" viewAllLink="/events" id="events-heading" />
          {eventsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-[180px] md:h-[200px] rounded-xl" />
              ))}
            </div>
          ) : upcomingEvents && upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No upcoming events.</p>
          )}
        </section>
      </main>
    </Layout>
  );
}
